import React from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
} from './components/base_components';
import { BusyOverlay, useBusy } from './components/busy_component';
import ProgressSpinner from './components/progress_spinner';
import Breadcrumb from './components/breadcrumb';
import StandardBoxList from './components/standard_box_list';
import TabBar from './components/tab_bar';
import TitleActionLine from './components/title_action_line';
import TextButton from './components/buttons/text_button';

import MediaGrantStore from './stores/media_grant_store';
import UserStore from './stores/user_store';
import VolumeStore from './stores/volume_store';

const styles = StyleSheet.create({
  volumeListScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  tabBar: {
    marginLeft: '2rem',
    marginRight: '2rem',
  },
  dialog: {
    marginLeft: '2rem',
    marginBottom: '2rem',
    flexDirection: 'column',
  },
  text: {
    marginTop: '1em',
    fontSize: '1.5em',
  },
  input: {
    alignSelf: 'stretch',
    maxWidth: '40rem',
    marginTop: '1rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '4rem',
    color: '#555',
    fontSize: '1.5rem',
    borderRadius: '0.5rem',
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#f5f5f5',
  },
  button: {
    marginTop: '1rem',
    alignSelf: 'flex-start',
  },
});

export default function VolumeListScreen() {
  const org = UserStore.useOrg();
  const volume_list = VolumeStore.useVolumeList(org?.org_id);
  const [volume_name, setVolumeName] = React.useState('');
  const [is_busy, setBusy, clearBusy] = useBusy();

  React.useEffect(() => {
    VolumeStore.fetch(org?.org_id);
  }, [org?.org_id]);

  function _onCreatePress() {
    if (org && volume_name && setBusy()) {
      const opts = {
        org_id: org.org_id,
        volume_name,
      };
      VolumeStore.createVolume(opts, (err, volume_id) => {
        if (err === 402) {
          window.alert('Sorry, your plan limit for volumes has been reached.');
        } else if (err === 409) {
          window.alert('Name already in use, please use another.');
        } else if (err) {
          window.alert('Failed to create volume, please try again.');
        } else {
          setVolumeName('');
          MediaGrantStore.fetch(org?.org_id);
        }
        clearBusy();
      });
    }
  }

  let content: any = null;
  let headers;
  const list = volume_list ?? [];
  if (!volume_list) {
    content = <ProgressSpinner />;
  } else if (list.length > 0) {
    headers = ['Volume Name', 'Status'];
  } else if (org?.is_admin && volume_list.length === 0) {
    content = (
      <View style={styles.dialog}>
        <Text style={styles.text}>
          Lets get started and create your first volume!
        </Text>
        <TextInput
          style={styles.input}
          placeholder='Volume Name'
          value={volume_name}
          onChangeText={setVolumeName}
        />
        <TextButton
          style={styles.button}
          text='Create Volume'
          onPress={_onCreatePress}
        />
      </View>
    );
  } else {
    content = (
      <View style={styles.dialog}>
        <Text style={styles.text}>
          Looks like you don't have access to any volumes.
        </Text>
        <Text style={styles.text}>
          Contract your organizations administrator to get access.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.volumeListScreen}>
      <Breadcrumb list={['Volumes']} />
      <TitleActionLine title='Volumes' />
      <TabBar
        style={styles.tabBar}
        list={[
          { text: 'Volumes', value: 'volume', url: '/volume' },
          { text: 'Permissions', value: 'grant', url: '/volume/permission' },
        ]}
      />
      <StandardBoxList
        title='Volumes'
        detail={list?.length}
        headers={headers}
        rows={list?.map?.((v: any) => ({
          url: `/volume/${v?.volume_id}`,
          list: [v?.volume_name, v.is_disabled ? 'Disabled' : 'Enabled'],
        }))}
      >
        {content}
      </StandardBoxList>
      <StandardBoxList title='Add Volume'>
        <View style={styles.dialog}>
          <Text style={styles.text}>
            Create a new volume for your organization.
          </Text>
          <TextInput
            style={styles.input}
            placeholder='New Volume Name'
            value={volume_name}
            onChangeText={setVolumeName}
          />
          <TextButton
            style={styles.button}
            text='Create Volume'
            onPress={_onCreatePress}
          />
        </View>
      </StandardBoxList>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
